/* Home stats, search chips, product rail controls and accessible scent style tabs. */
(() => {
  'use strict';
  if (window.SC3Home?.initialized) { window.SC3Home.init(document); return; }

  const offerAnimations = new Map();
  const statsAnimations = new Map();
  function initStats(el) {
    if (el.dataset.sc3StatsReady) return;
    el.dataset.sc3StatsReady = 'true';
    const motion = matchMedia('(prefers-reduced-motion: reduce)');
    if (motion.matches || !('IntersectionObserver' in window)) return;
    const counters = Array.from(el.querySelectorAll('[data-sc3-stat-count]')).flatMap(node => {
      const finalText = node.textContent;
      const parts = finalText.match(/^([^\d]*)(\d[\d,]*(?:\.\d+)?)([^\d]*)$/);
      if (!parts) return [];
      const target = Number(parts[2].replace(/,/g, ''));
      if (!Number.isFinite(target)) return [];
      const decimals = (parts[2].split('.')[1] || '').length;
      return [{ node, finalText, target, decimals, prefix: parts[1], suffix: parts[3], grouped: parts[2].includes(',') }];
    });
    if (!counters.length) return;
    let frame = null, started = false;
    const observer = new IntersectionObserver(entries => {
      if (started || !entries.some(entry => entry.isIntersecting)) return;
      started = true;
      observer.disconnect();
      if (motion.matches) { finish(); return; }
      const start = performance.now();
      draw(0);
      function tick(now) {
        if (!el.isConnected || motion.matches) { finish(); return; }
        const progress = Math.min(Math.max((now - start) / 900, 0), 1);
        if (progress === 1) { finish(); return; }
        draw(1 - Math.pow(1 - progress, 3));
        frame = requestAnimationFrame(tick);
      }
      frame = requestAnimationFrame(tick);
    }, { threshold: 0 });
    function draw(progress) {
      counters.forEach(counter => {
        const scale = Math.pow(10, counter.decimals);
        const value = Math.floor(counter.target * progress * scale) / scale;
        const parts = value.toFixed(counter.decimals).split('.');
        if (counter.grouped) parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        counter.node.textContent = counter.prefix + parts.join('.') + counter.suffix;
      });
    }
    function finish() {
      if (frame !== null) cancelAnimationFrame(frame);
      observer.disconnect();
      motion.removeEventListener('change', motionChanged);
      counters.forEach(counter => { counter.node.textContent = counter.finalText; });
      statsAnimations.delete(el);
    }
    function motionChanged() { if (motion.matches) finish(); }
    motion.addEventListener('change', motionChanged);
    statsAnimations.set(el, finish);
    observer.observe(el);
  }
  function initOffer(el) {
    if (offerAnimations.has(el)) return;
    const bottles = Array.from(el.querySelectorAll('.sc3-offer-anim__b'));
    const tiers = Array.from(el.querySelectorAll('[data-at]'));
    const fill = el.querySelector('[data-sc3-offer-fill]');
    const total = bottles.length;
    if (!total) return;
    const marks = tiers.map(tier => Number(tier.dataset.at));
    const motion = matchMedia('(prefers-reduced-motion: reduce)');
    bottles.forEach((bottle, index) => bottle.classList.toggle('is-milestone', marks.includes(index + 1)));
    let step = 0, timer = null, visible = false;
    function show(value) {
      bottles.forEach((bottle, index) => bottle.classList.toggle('is-on', index < value));
      tiers.forEach((tier, index) => tier.classList.toggle('is-on', value >= marks[index]));
      fill.style.inlineSize = (value / total * 100) + '%';
    }
    function stop() { clearTimeout(timer); timer = null; }
    function tick() {
      stop();
      if (!visible || document.hidden || motion.matches) return;
      step = step >= total ? 0 : step + 1;
      show(step);
      timer = setTimeout(tick, step === total ? 2600 : step === 0 ? 700 : 480);
    }
    function resume() {
      stop();
      if (motion.matches) { show(total); return; }
      if (visible && !document.hidden) tick();
    }
    const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
      visible = entries[0].isIntersecting;
      if (visible) resume(); else stop();
    }, { threshold: 0.15 }) : null;
    function motionChanged() {
      step = 0;
      if (motion.matches || !observer) { stop(); show(total); }
      else { show(step); resume(); }
    }
    motion.addEventListener('change', motionChanged);
    document.addEventListener('visibilitychange', resume);
    if (observer) { motionChanged(); observer.observe(el); } else show(total);
    offerAnimations.set(el, () => {
      stop();
      observer?.disconnect();
      motion.removeEventListener('change', motionChanged);
      document.removeEventListener('visibilitychange', resume);
      offerAnimations.delete(el);
    });
  }

  function init(scope) {
    scope.querySelectorAll('[data-sc3-home-stats]').forEach(initStats);
    scope.querySelectorAll('[data-sc3-offer-anim]').forEach(initOffer);
    scope.querySelectorAll('[data-sc3-home-tabs]').forEach(group => {
      if (group.dataset.sc3TabsReady) return;
      const tablist = group.querySelector('[role="tablist"]');
      const tabs = Array.from(tablist.querySelectorAll('[role="tab"]'));
      const panels = Array.from(group.querySelectorAll('[role="tabpanel"]'));
      if (!tabs.length) return;
      group.dataset.sc3TabsReady = 'true';
      const activate = (tab, focus = false) => {
        tabs.forEach(item => {
          const selected = item === tab;
          item.setAttribute('aria-selected', String(selected));
          item.tabIndex = selected ? 0 : -1;
        });
        panels.forEach(panel => {
          const selected = panel.id === tab.getAttribute('aria-controls');
          const template = selected ? panel.querySelector('template') : null;
          if (template) {
            panel.appendChild(template.content);
            template.remove();
          }
          panel.hidden = !selected;
          // Initialize inserted rail content only after its panel is visible.
          if (template) init(panel);
        });
        if (focus) tab.focus();
      };
      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => activate(tab));
        tab.addEventListener('keydown', event => {
          let next;
          const rtl = getComputedStyle(tablist).direction === 'rtl';
          if (event.key === 'ArrowRight') next = index + (rtl ? -1 : 1);
          else if (event.key === 'ArrowLeft') next = index + (rtl ? 1 : -1);
          else if (event.key === 'Home') next = 0;
          else if (event.key === 'End') next = tabs.length - 1;
          else return;
          event.preventDefault();
          activate(tabs[(next + tabs.length) % tabs.length], true);
        });
      });
      activate(tabs.find(tab => tab.getAttribute('aria-selected') === 'true') || tabs[0]);
    });
    scope.querySelectorAll('[data-sc3-home-rail]').forEach(track => {
      if (track.dataset.sc3HomeReady) return;
      track.dataset.sc3HomeReady = 'true';
      const frame = track.parentElement;
      const move = direction => {
        const item = track.querySelector('.sc3-home-rail-item, .sc3-rev');
        const step = item ? item.getBoundingClientRect().width + (parseFloat(getComputedStyle(track).columnGap) || 0) : track.clientWidth;
        const rtl = getComputedStyle(track).direction === 'rtl';
        track.scrollBy({ left: step * direction * (rtl ? -1 : 1), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      };
      frame.querySelector('[data-sc3-home-prev]')?.addEventListener('click', () => move(-1));
      frame.querySelector('[data-sc3-home-next]')?.addEventListener('click', () => move(1));
    });
  }
  document.addEventListener('click', event => {
    const button = event.target.closest('[data-sc3-scroll-prev], [data-sc3-scroll-next]');
    if (!button) return;
    const track = document.getElementById(button.getAttribute('aria-controls'));
    if (!track) return;
    const direction = button.hasAttribute('data-sc3-scroll-next') ? 1 : -1;
    const rtl = getComputedStyle(track).direction === 'rtl';
    track.scrollBy({ left: track.clientWidth * direction * (rtl ? -1 : 1), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  });
  document.addEventListener('shopify:section:unload', event => {
    statsAnimations.forEach((dispose, el) => { if (event.target.contains(el)) dispose(); });
    offerAnimations.forEach((dispose, el) => { if (event.target.contains(el)) dispose(); });
  });
  window.SC3Home = { initialized: true, init };
  document.addEventListener('click', event => {
    const chip = event.target.closest('[data-sc3-search-term]');
    if (!chip) return;
    const term = chip.dataset.sc3SearchTerm;
    setTimeout(() => {
      const dialog = document.querySelector('[data-sc3-dialog="search"]:not([hidden])');
      const input = dialog?.querySelector('[data-sc3-query]');
      if (!input) return;
      input.value = term;
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.focus();
    }, 0);
  }, true);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document));
  else init(document);
  document.addEventListener('shopify:section:load', event => init(event.target));
})();
